import type { ScopedSamples } from '@dealstack/shared/lib/documents/fieldBindingCatalogue.ts';
import { useState } from 'react';
import { ActionIcon } from '@mantine/core';
import { IconTrash, IconX } from '@tabler/icons-react';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { BindingBuilder } from './BindingBuilder.tsx';
import type {
	DataPointWithSampleRes,
	FieldBindingRes,
	SecurityOptionRes,
} from './BindPanel.types.ts';
import {
	EyebrowLabel,
	Header,
	HeaderActions,
	HeaderRow,
	LabelInput,
	ScrollBody,
	Wrapper,
} from './bindPanel.styled.ts';

interface Props {
	field: FieldBindingRes | null;
	catalogue: DataPointWithSampleRes[];
	onChangeBinding: (fieldKey: string, bindingTemplate: string) => void;
	onRenameField: (fieldKey: string, label: string) => void;
	onRemove: () => void;
	onClose: () => void;
	/** Threaded straight through to {@link BindingBuilder} — see its Props for
	 * what each does. Optional so consumers with no programme (the template
	 * editor) can omit them. */
	securities?: SecurityOptionRes[];
	scopedSamples?: ScopedSamples;
	onApplySecurityToAll?: (securityId: string) => void;
	uncollectedRequirements?: readonly string[];
}

/**
 * The field-form binding panel used by master documents: the selected field's
 * label around the reusable {@link BindingBuilder}. Save lives in
 * {@link BindPanelFooter}, outside this panel, so it survives deselecting. The
 * template editor consumes {@link BindingBuilder} directly instead, since
 * inline tokens have no field label.
 */
export const BindPanel = ({
	field,
	catalogue,
	onChangeBinding,
	onRenameField,
	onRemove,
	onClose,
	securities,
	scopedSamples,
	onApplySecurityToAll,
	uncollectedRequirements,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	// Seeded from field.label; BindPanel remounts per selected field (parent
	// keys it by fieldKey) so this local echo never goes stale across fields.
	const [labelDraft, setLabelDraft] = useState(() => field?.label ?? '');

	if (!field) {
		return null;
	}

	const commitLabel = () => {
		const trimmed = labelDraft.trim();
		if (trimmed && trimmed !== field.label) {
			onRenameField(field.fieldKey, trimmed);
		}
	};

	return (
		<Wrapper data-testid="bind-panel">
			<Header>
				<EyebrowLabel>
					{tMipManage('master_documents.field_binding.panel.eyebrow')}
				</EyebrowLabel>
				<HeaderRow>
					<LabelInput
						aria-label={tMipManage(
							'master_documents.field_binding.panel.field_label_aria'
						)}
						value={labelDraft}
						onChange={(event) => setLabelDraft(event.currentTarget.value)}
						onBlur={commitLabel}
					/>
					<HeaderActions>
						<ActionIcon
							className="trash-action-icon"
							variant="subtle"
							color="red"
							aria-label={tMipManage(
								'master_documents.field_binding.panel.remove_field_aria'
							)}
							onClick={onRemove}
						>
							<IconTrash size={16} />
						</ActionIcon>
						<ActionIcon
							variant="subtle"
							color="gray"
							aria-label={tMipManage(
								'master_documents.field_binding.panel.close_aria'
							)}
							onClick={onClose}
						>
							<IconX size={16} />
						</ActionIcon>
					</HeaderActions>
				</HeaderRow>
			</Header>

			<ScrollBody>
				<BindingBuilder
					bindingTemplate={field.bindingTemplate}
					catalogue={catalogue}
					onChange={(bindingTemplate) => onChangeBinding(field.fieldKey, bindingTemplate)}
					securities={securities}
					scopedSamples={scopedSamples}
					onApplySecurityToAll={onApplySecurityToAll}
					uncollectedRequirements={uncollectedRequirements}
				/>
			</ScrollBody>
		</Wrapper>
	);
};
